import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { MessageSquare, Users, Settings, Webhook, Plus, Trash2, Check, X, Globe, Database, MessageCircle, RefreshCw, AlertTriangle, ExternalLink, Zap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

interface ChatwootContact {
  id: string;
  chatwoot_id: number;
  name: string;
  email?: string;
  phone?: string;
  identifier?: string;
  contact_type: 'lead' | 'customer';
  created_at: string;
  updated_at: string;
  additional_attributes?: any;
  request_id?: string;
  chatwoot_requests?: {
    id: string;
    request_number: string;
    status: string;
    client_id?: string;
  };
}

interface ChatwootMessage {
  id: string;
  chatwoot_message_id: number;
  conversation_id: number;
  contact_id: number;
  content: string;
  message_type: string;
  sender_type: string;
  sender_name: string;
  created_at: string;
}

// Component for diagnostics tab
function DiagnosticsTab({ getWebhookUrl, testWebhook, contacts, messages }: {
  getWebhookUrl: () => string;
  testWebhook: () => Promise<void>;
  contacts: ChatwootContact[];
  messages: ChatwootMessage[];
}) {
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  const runDiagnostics = async () => {
    try {
      setTesting(true);
      console.log('Running comprehensive diagnostics...');
      
      const [eventsCount, contactsCount, messagesCount, recentEvents] = await Promise.all([
        supabase.from('chatwoot_events').select('*', { count: 'exact' }),
        supabase.from('chatwoot_contacts').select('*', { count: 'exact' }),
        supabase.from('chatwoot_messages').select('*', { count: 'exact' }),
        supabase.from('chatwoot_events').select('*').order('processed_at', { ascending: false }).limit(10)
      ]);

      const hasRealEvents = recentEvents.data?.some(event => event.event_type !== 'test_connection') || false;
      const eventTypes = [...new Set(recentEvents.data?.map(e => e.event_type) || [])];

      setDiagnostics({
        eventsTotal: eventsCount.count || 0,
        contactsTotal: contactsCount.count || 0,
        messagesTotal: messagesCount.count || 0,
        hasRealEvents,
        eventTypes,
        recentEvents: recentEvents.data || [],
        lastEventTime: recentEvents.data?.[0]?.processed_at,
        webhookUrl: getWebhookUrl(),
        chatwootUrl: 'https://chat.teletel.io'
      });
      
    } catch (error) {
      console.error('خطأ في التشخيص:', error);
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    runDiagnostics();
  }, []);

  if (!diagnostics) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>جاري إجراء التشخيص...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* حالة الاتصال */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            حالة الاتصال مع Chatwoot
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <span>إجمالي الأحداث</span>
              <Badge variant={diagnostics.eventsTotal > 0 ? "default" : "secondary"}>
                {diagnostics.eventsTotal}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <span>جهات الاتصال</span>
              <Badge variant={diagnostics.contactsTotal > 0 ? "default" : "secondary"}>
                {diagnostics.contactsTotal}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <span>الرسائل</span>
              <Badge variant={diagnostics.messagesTotal > 0 ? "default" : "secondary"}>
                {diagnostics.messagesTotal}
              </Badge>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {diagnostics.eventsTotal > 0 ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <X className="h-4 w-4 text-red-500" />
              )}
              <span>اتصال Webhook</span>
              <Badge variant={diagnostics.eventsTotal > 0 ? "default" : "destructive"}>
                {diagnostics.eventsTotal > 0 ? "متصل" : "غير متصل"}
              </Badge>
            </div>

            <div className="flex items-center gap-2">
              {diagnostics.hasRealEvents ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
              )}
              <span>أحداث حقيقية</span>
              <Badge variant={diagnostics.hasRealEvents ? "default" : "secondary"}>
                {diagnostics.hasRealEvents ? "يستقبل بيانات" : "اختبارات فقط"}
              </Badge>
            </div>
          </div>

          {!diagnostics.hasRealEvents && diagnostics.eventsTotal > 0 && (
            <div className="border-yellow-200 bg-yellow-50 border rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-yellow-800 mb-2">الـ webhook متصل لكن لا يستقبل أحداث حقيقية</div>
                  <div className="text-sm text-yellow-700 space-y-1">
                    <div>يرجى التأكد من إعداد الأحداث التالية في Chatwoot:</div>
                    <ul className="list-disc list-inside space-y-1 mt-2">
                      <li>contact_created ✓</li>
                      <li>contact_updated ✓</li>
                      <li>message_created ✓</li>
                      <li>conversation_created ✓</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {diagnostics.hasRealEvents && (
            <div className="border-green-200 bg-green-50 border rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600" />
                <div className="font-semibold text-green-800">
                  الـ webhook يعمل بشكل صحيح ويستقبل البيانات من Chatwoot
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button onClick={testWebhook} size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              اختبار الاتصال
            </Button>
            <Button onClick={runDiagnostics} disabled={testing} variant="outline" size="sm">
              <Zap className={`h-4 w-4 mr-2 ${testing ? 'animate-spin' : ''}`} />
              إعادة التشخيص
            </Button>
            <Button 
              onClick={() => window.open(`${diagnostics.chatwootUrl}/app/accounts/1/settings/integrations/webhooks`, '_blank')}
              variant="outline" 
              size="sm"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              إعدادات Webhook
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* معلومات تفصيلية */}
      <Card>
        <CardHeader>
          <CardTitle>معلومات تفصيلية</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-muted-foreground space-y-2">
            <div className="flex justify-between">
              <span><strong>رابط Webhook:</strong></span>
              <code className="text-xs bg-muted px-2 py-1 rounded">{diagnostics.webhookUrl}</code>
            </div>
            <div className="flex justify-between">
              <span><strong>آخر حدث:</strong></span>
              <span>{diagnostics.lastEventTime ? new Date(diagnostics.lastEventTime).toLocaleString('ar') : 'لا يوجد'}</span>
            </div>
            <div className="flex justify-between">
              <span><strong>أنواع الأحداث:</strong></span>
              <span>{diagnostics.eventTypes.join(', ') || 'لا يوجد'}</span>
            </div>
          </div>

          {diagnostics.recentEvents.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">آخر الأحداث:</h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {diagnostics.recentEvents.slice(0, 5).map((event: any, index: number) => (
                  <div key={index} className="text-xs p-2 bg-muted rounded flex justify-between">
                    <span>{event.event_type}</span>
                    <span>{new Date(event.processed_at).toLocaleString('ar')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* إرشادات الإعداد */}
      <Card>
        <CardHeader>
          <CardTitle>إرشادات الإعداد</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm space-y-3">
            <div>
              <h4 className="font-semibold">1. افتح إعدادات Chatwoot:</h4>
              <p className="text-muted-foreground">اذهب إلى Settings → Integrations → Webhooks</p>
            </div>
            <div>
              <h4 className="font-semibold">2. أضف webhook جديد:</h4>
              <p className="text-muted-foreground">استخدم الرابط: {diagnostics.webhookUrl}</p>
            </div>
            <div>
              <h4 className="font-semibold">3. فعّل هذه الأحداث:</h4>
              <ul className="text-muted-foreground list-disc list-inside space-y-1">
                <li>contact_created</li>
                <li>contact_updated</li>
                <li>message_created</li>
                <li>conversation_created</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold">4. اختبر النظام:</h4>
              <p className="text-muted-foreground">أنشئ عميل جديد أو أرسل رسالة في Chatwoot</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ChatwootClientsPage() {
  const { toast } = useToast();
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState({
    conversation_created: true,
    conversation_status_changed: true,
    conversation_updated: false,
    message_created: true,
    message_updated: false,
    webwidget_triggered: false,
    contact_created: true,
    contact_updated: true,
  });
  const [isConnected, setIsConnected] = useState(false);

  // جلب جهات الاتصال من قاعدة البيانات
  const { data: contacts = [], refetch: refetchContacts, isLoading: contactsLoading, error: contactsError } = useQuery({
    queryKey: ["chatwoot-contacts"],
    queryFn: async () => {
      console.log('Fetching Chatwoot contacts...');
      const { data, error } = await supabase
        .from('chatwoot_contacts')
        .select(`
          *,
          chatwoot_requests:request_id (
            id,
            request_number,
            status,
            client_id
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching contacts:', error);
        throw error;
      }
      
      console.log('Contacts fetched:', data);
      return (data as ChatwootContact[]) || [];
    },
  });

  // جلب الرسائل من قاعدة البيانات
  const { data: messages = [], isLoading: messagesLoading, error: messagesError } = useQuery({
    queryKey: ["chatwoot-messages"],
    queryFn: async () => {
      console.log('Fetching Chatwoot messages...');
      const { data, error } = await supabase
        .from('chatwoot_messages')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) {
        console.error('Error fetching messages:', error);
        throw error;
      }
      
      console.log('Messages fetched:', data);
      return (data as ChatwootMessage[]) || [];
    },
  });

  useEffect(() => {
    const savedWebhook = localStorage.getItem('chatwoot-webhook-url');
    const savedEvents = localStorage.getItem('chatwoot-webhook-events');
    const savedConnectionStatus = localStorage.getItem('chatwoot-connected');

    if (savedWebhook) setWebhookUrl(savedWebhook);
    if (savedEvents) {
      try {
        setWebhookEvents(JSON.parse(savedEvents));
      } catch (e) {
        console.error('Error parsing webhook events:', e);
      }
    }
    if (savedConnectionStatus) {
      setIsConnected(savedConnectionStatus === 'true');
    }
  }, []);

  const saveWebhookConfiguration = () => {
    if (!webhookUrl.trim()) {
      toast({
        title: "خطأ",
        description: "يرجى إدخال رابط Webhook صالح",
        variant: "destructive",
      });
      return;
    }

    try {
      new URL(webhookUrl);
    } catch {
      toast({
        title: "خطأ",
        description: "رابط Webhook غير صالح",
        variant: "destructive",
      });
      return;
    }

    localStorage.setItem('chatwoot-webhook-url', webhookUrl);
    localStorage.setItem('chatwoot-webhook-events', JSON.stringify(webhookEvents));
    setIsConnected(true);
    localStorage.setItem('chatwoot-connected', 'true');

    toast({
      title: "تم الحفظ",
      description: "تم حفظ إعدادات Webhook بنجاح",
    });
  };

  const disconnectWebhook = () => {
    localStorage.removeItem('chatwoot-webhook-url');
    localStorage.removeItem('chatwoot-webhook-events');
    localStorage.removeItem('chatwoot-connected');
    setWebhookUrl('');
    setIsConnected(false);
    
    toast({
      title: "تم قطع الاتصال",
      description: "تم قطع الاتصال مع Chatwoot",
    });
  };

  const handleEventToggle = (eventType: string, enabled: boolean) => {
    setWebhookEvents(prev => ({
      ...prev,
      [eventType]: enabled
    }));
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const getContactTypeColor = (type: string) => {
    return type === 'customer' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
  };

  const getContactTypeLabel = (type: string) => {
    return type === 'customer' ? 'عميل' : 'عميل محتمل';
  };

  // الحصول على رابط Webhook الفعلي
  const getWebhookUrl = () => {
    return `https://oensydfsjipboyzupgnm.supabase.co/functions/v1/chatwoot-webhook`;
  };

  // Test webhook with comprehensive diagnostics
  const testWebhook = async () => {
    try {
      console.log('Starting comprehensive webhook test...');
      
      // First, test direct webhook functionality
      const { data, error } = await supabase.functions.invoke('chatwoot-webhook', {
        body: {
          event_type: 'test_connection',
          data: {
            message: 'Test connection from dashboard'
          }
        }
      });

      if (error) {
        console.error('Direct webhook test error:', error);
        throw error;
      }

      console.log('Direct webhook test response:', data);
      
      // Get diagnostic information
      const [eventsCount, contactsCount, messagesCount, recentEvents] = await Promise.all([
        supabase.from('chatwoot_events').select('*', { count: 'exact' }),
        supabase.from('chatwoot_contacts').select('*', { count: 'exact' }),
        supabase.from('chatwoot_messages').select('*', { count: 'exact' }),
        supabase.from('chatwoot_events').select('*').order('processed_at', { ascending: false }).limit(5)
      ]);

      const hasRealEvents = recentEvents.data?.some(event => event.event_type !== 'test_connection') || false;
      const eventTypes = [...new Set(recentEvents.data?.map(e => e.event_type) || [])];

      console.log('Diagnostic results:', {
        events: eventsCount.count,
        contacts: contactsCount.count,
        messages: messagesCount.count,
        hasRealEvents,
        eventTypes,
        recentEvents: recentEvents.data
      });
      
      // Test comprehensive connection
      const { data: testData, error: testError } = await supabase.functions.invoke('test-webhook-connection', {
        body: {
          webhookUrl: getWebhookUrl(),
          chatwootUrl: 'https://chat.teletel.io'
        }
      });

      if (testError) {
        console.error('Comprehensive test error:', testError);
        throw testError;
      }

      console.log('Comprehensive test results:', testData);
      
      // Generate detailed feedback based on results
      if (hasRealEvents) {
        toast({
          title: "✅ الاتصال يعمل بالكامل",
          description: `تم العثور على ${contactsCount.count} جهة اتصال و ${messagesCount.count} رسالة`,
        });
      } else if (eventsCount.count > 0) {
        toast({
          title: "⚠️ اتصال جزئي",
          description: "الـ webhook متصل لكن لا يستقبل أحداث حقيقية. تحقق من إعدادات الأحداث في Chatwoot",
        });
      } else {
        toast({
          title: "❌ لا يوجد اتصال",
          description: "لم يتم استقبال أي أحداث. تحقق من إعداد الـ webhook في Chatwoot",
        });
      }
      
    } catch (error) {
      console.error('Webhook test failed:', error);
      toast({
        title: "فشل الاختبار",
        description: "فشل في اختبار الاتصال مع Webhook",
        variant: "destructive",
      });
    }
  };

  if (contactsError || messagesError) {
    console.error('Database errors:', { contactsError, messagesError });
  }

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
          <MessageSquare className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">عملاء Chatwoot</h1>
          <p className="text-muted-foreground mt-1">
            إدارة العملاء المستوردين من منصة Chatwoot
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{contactsLoading ? '...' : contacts.length}</p>
                <p className="text-sm text-gray-600">إجمالي العملاء</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {contactsLoading ? '...' : contacts.filter(c => c.contact_type === 'customer').length}
                </p>
                <p className="text-sm text-gray-600">عملاء</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{messagesLoading ? '...' : messages.length}</p>
                <p className="text-sm text-gray-600">الرسائل</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${contacts.length > 0 ? 'bg-green-100' : messages.length > 0 ? 'bg-yellow-100' : 'bg-red-100'} rounded-lg flex items-center justify-center`}>
                <Globe className={`w-5 h-5 ${contacts.length > 0 ? 'text-green-600' : messages.length > 0 ? 'text-yellow-600' : 'text-red-600'}`} />
              </div>
              <div>
                <p className="text-sm font-medium">
                  {contacts.length > 0 ? 'متصل ويعمل' : messages.length > 0 ? 'متصل جزئياً' : 'غير متصل'}
                </p>
                <p className="text-xs text-gray-600">حالة البيانات</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="contacts" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-5">
          <TabsTrigger value="contacts" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            العملاء
          </TabsTrigger>
          <TabsTrigger value="messages" className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            الرسائل
          </TabsTrigger>
          <TabsTrigger value="diagnostics" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            التشخيص
          </TabsTrigger>
          <TabsTrigger value="webhook" className="flex items-center gap-2">
            <Webhook className="w-4 h-4" />
            إعدادات Webhook
          </TabsTrigger>
          <TabsTrigger value="events" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            الأحداث
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contacts">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>قائمة العملاء</CardTitle>
                  <CardDescription>العملاء المستوردين من منصة Chatwoot</CardDescription>
                </div>
                <Button onClick={() => refetchContacts()} variant="outline" size="sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  تحديث
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {contactsLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-4 animate-spin" />
                  <p className="text-gray-600">جاري تحميل العملاء...</p>
                </div>
              ) : contactsError ? (
                <div className="text-center py-8">
                  <X className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <p className="text-red-600 mb-2">خطأ في تحميل العملاء</p>
                  <p className="text-sm text-gray-500">{contactsError.message}</p>
                </div>
              ) : contacts.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">لا توجد عملاء مستوردين</p>
                  <p className="text-sm text-gray-500 mt-2">
                    قم بإعداد Webhook لاستيراد العملاء من Chatwoot
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{contact.name}</h3>
                          <div className="flex flex-col gap-1 mt-1">
                            <p className="text-sm text-gray-600">ID: {contact.identifier || contact.chatwoot_id}</p>
                            {contact.chatwoot_requests && (
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {contact.chatwoot_requests.request_number}
                                </Badge>
                                <Badge variant={contact.chatwoot_requests.status === 'pending' ? 'secondary' : 'default'} className="text-xs">
                                  {contact.chatwoot_requests.status === 'pending' ? 'في الانتظار' : 'مكتمل'}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </div>
                        <Badge className={getContactTypeColor(contact.contact_type)}>
                          {getContactTypeLabel(contact.contact_type)}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        {contact.email && (
                          <div>
                            <span className="text-sm text-gray-500">البريد الإلكتروني:</span>
                            <p className="font-medium">{contact.email}</p>
                          </div>
                        )}
                        {contact.phone && (
                          <div>
                            <span className="text-sm text-gray-500">رقم الهاتف:</span>
                            <p className="font-medium" dir="ltr">{contact.phone}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-xs text-gray-500 flex flex-col md:flex-row md:justify-between gap-2">
                        <span>تم الإنشاء: {formatDate(contact.created_at)}</span>
                        <span>آخر تحديث: {formatDate(contact.updated_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="messages">
          <Card>
            <CardHeader>
              <CardTitle>سجل الرسائل</CardTitle>
              <CardDescription>الرسائل الواردة من Chatwoot</CardDescription>
            </CardHeader>
            <CardContent>
              {messagesLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-4 animate-spin" />
                  <p className="text-gray-600">جاري تحميل الرسائل...</p>
                </div>
              ) : messagesError ? (
                <div className="text-center py-8">
                  <X className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <p className="text-red-600 mb-2">خطأ في تحميل الرسائل</p>
                  <p className="text-sm text-gray-500">{messagesError.message}</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">لا توجد رسائل</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {messages.map((message) => (
                    <div key={message.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-medium">{message.sender_name}</div>
                        <div className="text-xs text-gray-500">{formatDate(message.created_at)}</div>
                      </div>
                      <p className="text-gray-700 mb-2">{message.content}</p>
                      <div className="flex gap-2">
                        <Badge variant="outline">{message.message_type}</Badge>
                        <Badge variant="outline">{message.sender_type}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="diagnostics">
          <DiagnosticsTab 
            getWebhookUrl={getWebhookUrl} 
            testWebhook={testWebhook}
            contacts={contacts}
            messages={messages}
          />
        </TabsContent>

        <TabsContent value="webhook">
          <Card>
            <CardHeader>
              <CardTitle>إعدادات Webhook</CardTitle>
              <CardDescription>
                استخدم الرابط التالي في إعدادات Chatwoot
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="webhook-url">رابط Webhook (للنسخ)</Label>
                <Input
                  id="webhook-url"
                  type="text"
                  value={getWebhookUrl()}
                  readOnly
                  className="mt-2 font-mono text-sm"
                />
                <p className="text-sm text-gray-500 mt-1">
                  انسخ هذا الرابط واستخدمه في إعدادات Webhook في Chatwoot
                </p>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={() => navigator.clipboard.writeText(getWebhookUrl())}
                  className="flex-1"
                >
                  <Database className="w-4 h-4 mr-2" />
                  نسخ الرابط
                </Button>
                <Button 
                  onClick={testWebhook}
                  variant="outline"
                  className="flex-1"
                >
                  <Check className="w-4 h-4 mr-2" />
                  اختبار الاتصال
                </Button>
              </div>

              {isConnected && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">جاهز للاستخدام</span>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    استخدم الرابط أعلاه في إعدادات Chatwoot لبدء استقبال البيانات
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events">
          <Card>
            <CardHeader>
              <CardTitle>إعدادات الأحداث</CardTitle>
              <CardDescription>اختر الأحداث التي تريد استقبالها من Chatwoot</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(webhookEvents).map(([eventType, enabled]) => (
                <div key={eventType} className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium">
                      {eventType === 'conversation_created' && 'إنشاء محادثة'}
                      {eventType === 'conversation_status_changed' && 'تغيير حالة المحادثة'}
                      {eventType === 'conversation_updated' && 'تحديث المحادثة'}
                      {eventType === 'message_created' && 'إنشاء رسالة'}
                      {eventType === 'message_updated' && 'تحديث الرسالة'}
                      {eventType === 'webwidget_triggered' && 'فتح أداة الدردشة'}
                      {eventType === 'contact_created' && 'إنشاء جهة اتصال'}
                      {eventType === 'contact_updated' && 'تحديث جهة اتصال'}
                    </div>
                    <div className="text-xs text-gray-500">{eventType}</div>
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={(checked) => handleEventToggle(eventType, checked)}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
